import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import {
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import PrintIcon from '@mui/icons-material/Print'
import { useState } from 'react'
import { api } from '@/api/client'

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/**
 * The booking diary, printed blank for the counter to write in.
 *
 * <p>The book is kept by hand and always has been: a page a day, two bookings
 * to a page, in the ruling the printer set. What this replaces is the trip to
 * the printer, not the writing - so it is a year, the months wanted, and a
 * PDF.</p>
 */
export function BookingDiaryCard() {
  const thisYear = new Date().getFullYear()
  const [year, setYear] = useState(String(thisYear))
  // None chosen is the whole year, which is how a diary is usually ordered.
  const [months, setMonths] = useState<number[]>([])
  const [printing, setPrinting] = useState(false)

  const toggle = (month: number) =>
    setMonths((current) =>
      current.includes(month)
        ? current.filter((one) => one !== month)
        : [...current, month].sort((a, b) => a - b),
    )

  const pages = months.length === 0 ? 365 : months.reduce((sum, month) => {
    const days = new Date(Number(year), month, 0).getDate()
    return sum + (Number.isNaN(days) ? 0 : days)
  }, 0)

  const params = () => ({ year, months: months.length > 0 ? months.join(',') : undefined })

  const print = async () => {
    setPrinting(true)
    try {
      await api.print('/api/v1/reports/booking-diary', params())
    } finally {
      setPrinting(false)
    }
  }

  /** The book as a file, for a printer somebody else is standing at. */
  const download = async () => {
    setPrinting(true)
    try {
      await api.download(
        '/api/v1/reports/booking-diary',
        params(),
        months.length === 1
          ? `diary-${year}-${String(months[0]).padStart(2, '0')}.pdf`
          : `diary-${year}.pdf`,
      )
    } finally {
      setPrinting(false)
    }
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle1">Booking diary</Typography>
        <Typography variant="body2" color="text.secondary">
          The book kept at the counter, printed blank: a page a day with the date at the head and
          the foot, and two bookings to a page. Choose the months, or take the whole year.
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            type="number"
            label="Year"
            value={year}
            onChange={(event) => setYear(event.target.value)}
            inputProps={{ min: 2000, max: 2100 }}
            sx={{ width: 120 }}
          />
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ flexGrow: 1 }}>
            {MONTHS.map((name, index) => {
              const month = index + 1
              const chosen = months.includes(month)
              return (
                <Chip
                  key={name}
                  size="small"
                  label={name.slice(0, 3)}
                  variant={chosen ? 'filled' : 'outlined'}
                  color={chosen ? 'primary' : 'default'}
                  onClick={() => toggle(month)}
                />
              )
            })}
          </Stack>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            disabled={printing || !year}
            onClick={() => void print()}
          >
            {printing ? 'Preparing…' : 'Print the diary'}
          </Button>
          <Button
            startIcon={<PictureAsPdfIcon />}
            disabled={printing || !year}
            onClick={() => void download()}
          >
            PDF
          </Button>
          {/* Said before it is asked for: a year is three hundred and
              sixty-five sheets, and somebody should know that before the
              printer starts. */}
          <Typography variant="caption" color="text.secondary">
            {months.length === 0
              ? `The whole of ${year} — about ${pages} pages`
              : `${months.length} ${months.length === 1 ? 'month' : 'months'} — ${pages} pages`}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  )
}
